import styled from "styled-components";
import {
  TableHeaderItem,
  TablePagination,
  TableSortBy,
  useTable,
} from "../../hooks/useTable";
import { Table } from "../organisms/table";
import { Pagination } from "../organisms/Pagination";
import { usePermissions } from "../../hooks/usePermissions";
import { Resource, Permission } from "../../constants/permissions";
import { usePermissionErrorHandler } from "../../utils/permissionErrors";
import { useResponsive } from "../../hooks/useResponsive";

type Props<DataType> = {
  title?: string;
  headers: TableHeaderItem<DataType>[];
  data: DataType[];
  loading?: boolean;
  defaultSort?: TableSortBy;
  selectable?: boolean;
  editable?: boolean;
  deletable?: boolean;
  hasPagination?: boolean;
  perPage?: number;
  total?: number;
  stickyHeader?: boolean;
  defaultEmptyText?: string;
  enableHoverPopup?: boolean;
  columnWidths?: string[]; // New prop for custom column widths
  showHeaderDeleteIcon?: boolean; // New prop to control header delete icon
  resource?: Resource; // Resource for permission checking
  onPermissionError?: (message: string) => void; // Callback for permission errors
  fetchDataFunction: (sort: TableSortBy, pagination?: TablePagination) => void;
  onEdit?: (data: DataType) => void;
  onDelete?: (data: DataType) => void;
  onSelect?: (data: DataType, checked: boolean) => void;
  onSelectAll?: (checked: boolean) => void;
  currentPage?: number;
  onSetPage?: (page: number) => void;
};

export const TableTemplate = <DataType extends {}>({
  title,
  headers,
  data,
  loading = false,
  selectable,
  defaultSort,
  editable,
  deletable,
  hasPagination,
  perPage,
  total,
  stickyHeader,
  defaultEmptyText,
  enableHoverPopup,
  columnWidths,
  showHeaderDeleteIcon,
  resource,
  onPermissionError,
  fetchDataFunction,
  onEdit,
  onDelete,
  onSelect,
  onSelectAll,
  currentPage: controlledPage,
  onSetPage,
}: Props<DataType>) => {
  const { canUpdate, canDelete } = usePermissions();
  const { checkPermissionAndShowError } = usePermissionErrorHandler();
  const { isMobile, isTablet } = useResponsive();

  // Apply permission-based restrictions
  const effectiveEditable = resource ? canUpdate(resource) && editable : editable;
  const effectiveDeletable = resource ? canDelete(resource) && deletable : deletable;

  // Handle edit with permission check
  const handleEdit = (data: DataType) => {
    if (resource && onPermissionError) {
      if (!checkPermissionAndShowError(resource, Permission.UPDATE, 'edit', onPermissionError)) {
        return;
      }
    }
    onEdit?.(data);
  };

  // Handle delete with permission check
  const handleDelete = (data: DataType) => {
    if (resource && onPermissionError) {
      if (!checkPermissionAndShowError(resource, Permission.DELETE, 'delete', onPermissionError)) {
        return;
      }
    }
    onDelete?.(data);
  };
  const {
    headersToShow,
    dataToShow,
    currentPage,
    totalPage,
    setCurrentPage,
    onNextPage,
    onPreviousPage,
    disableBulkDeleteButton,
  } = useTable<DataType>({
    headers,
    data,
    fetchDataFunction,
    defaultSort,
    perPage,
    total: total ?? data.length,
    currentPage: controlledPage,
  });

  // Custom pagination handlers that work with controlled pagination
  const handlePrevious = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      if (onSetPage) {
        onSetPage(newPage);
      } else {
        setCurrentPage(newPage);
      }
    }
  };

  const handleNext = () => {
    if (currentPage < totalPage) {
      const newPage = currentPage + 1;
      if (onSetPage) {
        onSetPage(newPage);
      } else {
        setCurrentPage(newPage);
      }
    }
  };

  return (
    <StyledContainer>
      <StyledTableTemplateContainer $stickyHeader={stickyHeader} $isMobile={isMobile}>
        {title && (
          <StyledHeader>
            <StyleTitleContainer>
              <StyledHeaderTitle>{title}</StyledHeaderTitle>
            </StyleTitleContainer>
          </StyledHeader>
        )}

        <StyledTableContainer $stickyHeader={stickyHeader} $isMobile={isMobile}>
          <Table<DataType>
            headers={headersToShow}
            data={dataToShow}
            loading={loading}
            selectable={selectable}
            editable={effectiveEditable}
            deletable={effectiveDeletable}
            stickyHeader={stickyHeader}
            defaultEmptyText={defaultEmptyText}
            disableDeleteButton={disableBulkDeleteButton}
            enableHoverPopup={enableHoverPopup}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onSelect={onSelect}
            onSelectAll={onSelectAll}
            columnWidths={columnWidths}
            showHeaderDeleteIcon={showHeaderDeleteIcon}
          />
        </StyledTableContainer>

        {hasPagination && !loading && (
          <StyledPaginationWrapper $isMobile={isMobile}>
            <Pagination
              page={currentPage}
              totalPage={totalPage}
              onSetPage={onSetPage ? onSetPage : setCurrentPage}
              onPrevious={handlePrevious}
              onNext={handleNext}
            />
          </StyledPaginationWrapper>
        )}
      </StyledTableTemplateContainer>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const StyledTableTemplateContainer = styled.div<{ $stickyHeader?: boolean; $isMobile?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow: hidden;
  background-color: #ffffff;
  
  @media (max-width: 768px) {
    gap: 16px;
  }

  @media (max-width: 480px) {
    gap: 12px;
  }
  
  ${({ $stickyHeader }) => ($stickyHeader ? `height: 100%; ` : ``)}
  
  ${({ $isMobile }) =>
    $isMobile
      ? `
      -webkit-overflow-scrolling: touch;
      `
      : ""}
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const StyledHeaderTitle = styled.div`
  font-size: 20px;
  font-weight: 400;
  line-height: 100%;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const StyledTableContainer = styled.div<{ $stickyHeader?: boolean; $isMobile?: boolean }>`
  ${({ $stickyHeader }) =>
    $stickyHeader ? `position: relative;` : ``}

  max-width: 100%;
  ${({ $stickyHeader }) =>
    $stickyHeader ? `
    max-height: calc(100vh - 200px);
    overflow-y: auto;
    overflow-x: auto;
    ` : `
    /* max-height: 100%; */
    /* height: 100%; */
    overflow: visible;
    `}
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 768px) {
    gap: 16px;
    ${({ $stickyHeader }) =>
      $stickyHeader ? `
      max-height: calc(100vh - 150px);
      ` : ``}
  }

  @media (max-width: 480px) {
    gap: 12px;
    ${({ $stickyHeader }) =>
      $stickyHeader ? `
      max-height: calc(100vh - 120px);
      ` : ``}
  }
  
  ${({ $isMobile }) =>
    $isMobile
      ? `
      -webkit-overflow-scrolling: touch;
      `
      : ""}
`;

const StyleTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledPaginationWrapper = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  margin-top: 8px;
  margin-bottom: 0;
  padding: 0 24px 0 0;

  @media (max-width: 768px) {
    justify-content: center;
    padding: 0 16px 0 0;
  }

  @media (max-width: 480px) {
    padding: 0 12px 0 0;
  }
  
  ${({ $isMobile }) =>
    $isMobile
      ? `
      -webkit-overflow-scrolling: touch;
      `
      : ""}
`;
